// MEET-LIVE-CALL-SURVIVES-SWITCH-1 — the meet package's copy of the canonical
// "drop a reply that outlived its tenant" writer.
//
// WHY MEET NEEDED ONE. Registering a key as workspace-scoped (the shell's
// state-app.js) wipes it ON the switch. It cannot stop a request ISSUED in
// workspace A from writing its reply into workspace B afterwards. `connectCall`
// is the longest such request in the app: a token mint, a livekit-client
// import, a getUserMedia prompt and a signal connect, any of which the user can
// switch workspaces through — and its completion write says "you are in a
// call", which is a claim about a tenant, not a banner.
//
// ⛔ THE GUARD IS ONLY SAFE BESIDE DISCONNECT-ON-SWITCH. Dropping the
// completion write ALONE would hide an ACTIVE LiveKit connection in B — a
// worse wedge than a stale banner, which is exactly why this guard was
// withheld until the switch path tore the call down first (see the shell's
// onWorkspaceChange). The caller must therefore treat a dropped write as
// "this connection was born into a tenant that no longer exists" and
// disconnect it, never as a silent no-op.
//
// ⛔ CAPTURE BEFORE THE AWAIT. `sameWorkspaceWriter(root)` must be called on
// the SAME tick as the request, before the first `await` — it snapshots the
// workspace id at call time. Calling it inside the `.then()` snapshots the NEW
// workspace and the guard can never fire.
//
// ⛔ WHY THIS FILE EXISTS AGAIN. The shell owns the canonical copy at
// workspace/functions/sameWorkspaceWriter.js; `tickets` and `chat` keep their
// own. `meet` is a SUB-APP and must not import the shell either, so it keeps
// this one. The CODE below is held identical across the copies (the drift pins
// strip comments and compare code) — documenting each copy in its own words is
// not a drift. Consumers inside this package import THIS module — never
// re-inline it.

// Both id shapes are live on root: `workspace.id` from the SDK's workspace
// payload and `workspace._id` from the raw Mongo document. Stringified so a
// numeric id and its string form never read as two different tenants.
export const wsIdOf = (root) => String(root?.workspace?.id || root?.workspace?._id || '')

// Returns a `write(patch)` that applies the patch only while the workspace is
// still the one it was created in. Returns `true` when the patch landed and
// `false` when it was dropped, so a caller can tell the two apart (a silent
// no-op reads as success at the call site).
export const meetSameWorkspaceWriter = (root) => {
  const issuedFor = wsIdOf(root)
  return (patch) => {
    if (wsIdOf(root) !== issuedFor) return false
    root.update(patch)
    return true
  }
}
